import { SupabaseClient, User, Session } from '@supabase/supabase-js';

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

export class Authorizer {
  private supabase: SupabaseClient;
  private treeId: string;

  constructor(supabase: SupabaseClient, treeId: string) {
    this.supabase = supabase;
    this.treeId = treeId;
  }

  /**
   * Gets the current user from Supabase
   * Supabase caches this internally, so it's not expensive
   */
  async getUser(): Promise<User | null> {
    const { data: { session } } = await this.supabase.auth.getSession();
    return session?.user ?? null;
  }

  /**
   * Gets the current session from Supabase
   */
  async getSession(): Promise<Session | null> {
    const { data: { session } } = await this.supabase.auth.getSession();
    return session;
  }

  getSupabase(): SupabaseClient {
    return this.supabase;
  }

  async signInWithEmail(email: string, redirectTo?: string): Promise<{ error: any }> {
    // Default to view mode (not edit) for the redirect
    const defaultRedirect = `${window.location.origin}/tree/${this.treeId}`;
    const redirect = redirectTo 
      ? `${window.location.origin}${redirectTo}`
      : defaultRedirect;
    
    const { error } = await this.supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirect,
      },
    });
    return { error };
  }

  async signOut(): Promise<void> {
    await this.supabase.auth.signOut();
  }

  async checkEntitlement(treeId: string): Promise<{ hasAccess: boolean; isOwner: boolean }> {
    console.log('Checking entitlement for treeId:', treeId);
    const { data, error } = await this.supabase.functions.invoke('check-entitlement', {
      body: { treeId }
    });

    if (error) {
      throw new Error(`Failed to check entitlement: ${error.message || 'Unknown error'}`);
    }

    if (!data || typeof data.hasAccess !== 'boolean' || typeof data.isOwner !== 'boolean') {
      throw new Error('Invalid response from check-entitlement function');
    }
    console.log('Entitlement check result:', data);

    return {
      hasAccess: data.hasAccess,
      isOwner: data.isOwner
    };
  }

  // No destroy() needed - no subscriptions to clean up
  // State is managed by React context
}
